import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { AdvanceFilters, BoxV2, TableListV2, useQueryFetch } from 'sonata-react-components';
import { DeviceOs } from 'sonata-static-storage/lib/common/common.types';

import { OsSelectionStyled } from './OsSelection.styled';

type Props = {
  getDeviceOs: () => Promise<DeviceOs[]>;
  id: string;
  onChangeDeviceOs: (newDeviceOs: DeviceOs[]) => void;
  selectedDeviceOs: DeviceOs['os'][];
};

type State = {
  filters: { text?: string };
};

export type DeviceOsDataEntry = { id: string } & DeviceOs;

const generateItemId = ({ normalizedOs, os }: DeviceOs): string => {
  return `${os}_${normalizedOs}`;
};

export function OsSelection({ getDeviceOs, id, onChangeDeviceOs, selectedDeviceOs }: Readonly<Props>) {
  const { formatMessage } = useIntl();

  const [{ filters }, setState] = useState<State>({
    filters: {
      text: undefined
    }
  });

  const { data = [], status } = useQueryFetch<DeviceOs[]>(`${id}-deviceOs`, getDeviceOs, {
    cacheTime: 0,
    retry: 0,
    staleTime: 0
  });

  const handleDeviceOsOnChanged = useCallback(
    (newSelection: DeviceOsDataEntry[]) => {
      onChangeDeviceOs(newSelection);
    },
    [onChangeDeviceOs]
  );

  const deviceOsColumns = useMemo(
    () => [
      {
        Cell: ({
          cell: {
            row: { original: rowData }
          }
        }: {
          cell: { row: { original: DeviceOsDataEntry } };
        }) => {
          const { normalizedOs, os } = rowData;
          return (
            <div className="deviceOsEntry textBodyDefault bodyDefault">
              <span title={os}>{normalizedOs}</span>
            </div>
          );
        },
        Header: formatMessage({
          defaultMessage: 'Name',
          id: 'targetingDeviceOs.list.header'
        }),
        accessor: 'deviceOsEntry',
        className: 'deviceOsEntry',
        customCell: true,
        textAlign: 'left'
      }
    ],
    [formatMessage]
  );

  const deviceOsColumnsData: DeviceOsDataEntry[] = useMemo(() => {
    if (status === 'success' && data) {
      return data.map(deviceOs => ({
        id: generateItemId(deviceOs),
        normalizedOs: deviceOs.normalizedOs,
        os: deviceOs.os
      }));
    }
    return [];
  }, [status, data, filters.text]);

  const defaultSelectedRows = useMemo(() => {
    return data
      .filter(deviceOs => selectedDeviceOs.includes(deviceOs.os))
      .reduce(
        (acc, deviceOs) => ({
          ...acc,
          [generateItemId(deviceOs)]: true
        }),
        {} as Record<string, boolean>
      );
  }, [selectedDeviceOs, deviceOsColumnsData, filters.text]);

  const handleAdvancedFiltersChanged = ({ text }: { text: string }) => {
    setState(prevState => ({
      ...prevState,
      filters: {
        ...prevState.filters,
        text: text || undefined
      }
    }));
  };

  const listFilters = useMemo(
    () => ({
      text: filters.text
    }),
    [filters.text]
  );

  return (
    <OsSelectionStyled className="deviceOsSelection">
      <AdvanceFilters className="deviceOsSelection__advancedFilters" onChange={handleAdvancedFiltersChanged} />
      <BoxV2 id={`${id}_device_os_box_inner_container`}>
        <BoxV2.Body className="deviceOsSelection__tableContainer" padding="0">
          {status !== 'loading' && (
            <TableListV2
              className="deviceOsSelection__tableContainer__tableData"
              columns={deviceOsColumns}
              config={{
                maxHeight: 450,
                numberOfColumns: 4,
                onChangeSelection: handleDeviceOsOnChanged,
                pagination: {
                  show: true,
                  size: 20,
                  type: 'compact'
                },
                searchEmptyState: {
                  description: formatMessage({
                    defaultMessage: 'Please change your search terms and try again',
                    id: 'targetingDeviceOs.list.emptyState.description'
                  }),
                  icon: 'fal fa-empty-set',
                  show: true,
                  title: formatMessage({
                    defaultMessage: 'Your search did not match any result',
                    id: 'targetingDeviceOs.list.emptyState.title'
                  })
                },
                selectableItem: true,
                toolbar: {
                  id: `${id}_toolbar`,
                  showSelectAll: true
                },
                type: 'list'
              }}
              data={deviceOsColumnsData}
              defaultSelectedRows={defaultSelectedRows}
              filters={listFilters}
              id={`${id}_deviceOsSelection_list`}
              isSelectableList
            />
          )}
        </BoxV2.Body>
      </BoxV2>
    </OsSelectionStyled>
  );
}
